import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { IState } from 'models/reducers/i-state.interface';

import { ROUTES } from 'constants/routes.constant';

import { ParticipationButton } from 'routes/referral/components/ParticipationButton';
import { getParticipationStatus } from 'routes/referral/helpers/map-user-status.helper';
import { useEventRegistration } from 'routes/referral/hooks/useEventRegistration';
import { useEventTypeRedirect } from 'routes/referral/hooks/useEventTypeRedirect';

import EventArenaSection from './components/EventArenaSection/EventArenaSection';
import IntroSection from './components/IntroSection/IntroSection';
import MyStatisticsSection from './components/MyStatisticsSection/MyStatisticsSection';
import ParticipationCriteriaSection from './components/ParticipationCriteriaSection/ParticipationCriteriaSection';
import PrizePoolSection from './components/PrizePoolSection/PrizePoolSection';
import Page from 'components/page/Page';
import PageWrapper from 'components/pageWrapper/PageWrapper';
import Spacing from 'components/spacing/Spacing';

import { useTradingEvent } from './hooks/useTradingEvent';
import { queryKeys } from 'hooks/queries/query-keys';
import RewardDistributionHistorySection from './components/RewardDistributionHistorySection/RewardDistributionHistorySection';

type TradingEventRouteParams = {
  id: string;
};

const TradingEventRoute = () => {
  const { id } = useParams<TradingEventRouteParams>();
  const navigate = useNavigate();
  const { event, loading, userStatus, eventStatus, eventType } = useTradingEvent({ eventId: id || '' });
  const [registrationError, setRegistrationError] = useState<string | null>(null);

  const walletAddress = useSelector((state: IState): string => state.wallet.address);
  const queryClient = useQueryClient();

  const { mutate: registerForEvent, isPending: isRegistering } = useEventRegistration({
    eventId: id!,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.event.detail(id!) });
      queryClient.invalidateQueries({ queryKey: queryKeys.event.userMetrics(id!) });
      queryClient.invalidateQueries({ queryKey: queryKeys.event.userStatus(id!) });
      setRegistrationError(null);
    },
    onError: (message) => {
      setRegistrationError(message);
    },
  });

  const participationStatus = useMemo(() => {
    return getParticipationStatus(eventStatus, registrationError, userStatus, walletAddress);
  }, [eventStatus, registrationError, userStatus, walletAddress]);

  const onClickRegister = () => {
    registerForEvent();
  };

  // Show estimated rewards only when user is logged in and has statistics
  const showEstimatedRewards = useMemo(() => {
    if (!walletAddress) return false;
    if (!event?.myStatistics?.estimatedReward?.length) return false;
    return true;
  }, [walletAddress, event?.myStatistics?.estimatedReward]);

  // Redirect to all events page if event is not found
  useEffect(() => {
    if (!event && !loading) {
      navigate(ROUTES.EVENTS, { replace: true });
    }
  }, [event, loading, navigate]);

  // Redirect to correct route based on event type
  useEventTypeRedirect({
    eventId: id,
    eventType,
    currentRoute: 'event',
    isLoading: loading,
  });

  return (
    <Page
      withScrollbar
      className="tw-overflow-x-hidden tw-text-center"
      pageBackground="dark"
      requireTermsOfServiceConsents
      loading={loading}
    >
      <PageWrapper homeFooter={false} landingFooter>
        {event && (
          <IntroSection
            name={event.intro.title}
            tagline={event.intro.subtitle}
            iconUrl={event.intro.iconUrl}
            imageClassName={event.intro.heroImageClassName}
            endAt={event.endAt}
            stages={event.stages}
            eventInfo={{
              eventId: id!,
              walletAddress,
              userStatus,
            }}
          >
            <div className="tw-mt-[40px] tw-flex tw-flex-col tw-items-center tw-gap-[16px]">
              <ParticipationButton
                participationStatus={participationStatus}
                isRegistering={isRegistering}
                onClickRegister={onClickRegister}
                registrationError={registrationError}
                eligibilityReasons={userStatus?.eligibility?.reasons}
                hasWallet={!!walletAddress}
              />
            </div>
          </IntroSection>
        )}
        <Spacing />
        <PrizePoolSection prizePool={event?.prizePool || []} featureHighlights={event?.featureHighlights || []} />
        {event && <MyStatisticsSection myStatistics={event.myStatistics} showEstimatedRewards={showEstimatedRewards} />}
        {event && <EventArenaSection leaderboard={event.leaderboard} rankRanges={event.rankRanges} />}
        <RewardDistributionHistorySection eventId={id} variant="light" />
        <Spacing height={60} />
        <div className="tw-bg-darkBlue">
          <Spacing />
          {event && <ParticipationCriteriaSection criteria={event.participationCriteria} />}
          <Spacing />
        </div>
      </PageWrapper>
    </Page>
  );
};

export default TradingEventRoute;
